import { aiGenerationProgressKey } from '@/features/datasets/utils/datasetOverviewProgress';
import { normalizeJobPhase } from '@/features/datasets/utils/generationPhaseLabels';

export const GENERATION_PHASE_UNDERSTANDING = 'understanding_the_task';
export const GENERATION_PHASE_GENERATING = 'generating';
export const GENERATION_PHASE_GENERATING_SAMPLES = 'generating_samples';
export const GENERATION_PHASE_CHECKING_LABELS = 'checking_labels';

/** Backend ``GenerationType`` values that reach the progress payload. */
export const GENERATION_TYPE_TRAJECTORY = 'trajectory';
export const GENERATION_TYPE_FULL_SAMPLE = 'full_sample';
export const GENERATION_TYPE_LABEL_ONLY = 'label_only';

export const TRAJECTORY_WARNING_TITLE = "Some trajectories couldn't be generated";
export const GENERATED_EXAMPLES_SKIPPED_TITLE = 'Some generated examples were skipped';

export function isTrajectoryGenerationType(generationType) {
    return generationType === GENERATION_TYPE_TRAJECTORY;
}

function finiteOrUndefined(raw) {
    if (raw == null) return undefined;
    const value = Number(raw);
    return Number.isFinite(value) ? value : undefined;
}

/**
 * Trajectory / summary fields of a generation progress payload. Accepts the
 * WebSocket relay (snake_case, same top-level object as ``progress`` and
 * ``processedPrompts``) and camelCase variants; every field is `undefined`
 * when the backend did not send it, so LABEL_ONLY / older payloads are unchanged.
 *
 * @returns {{
 *   generationType: string | undefined,
 *   generatedRows: number | undefined,
 *   skippedRows: number | undefined,
 *   skippedReasons: Record<string, number> | undefined,
 *   summary: string | undefined,
 * }}
 */
export function normalizeGenerationProgressPayload(event) {
    const generationType = event?.generation_type ?? event?.generationType;
    const skippedReasonsRaw = event?.skipped_reasons ?? event?.skippedReasons;
    const skippedReasons =
        skippedReasonsRaw && typeof skippedReasonsRaw === 'object' && !Array.isArray(skippedReasonsRaw)
            ? skippedReasonsRaw
            : undefined;
    const summaryRaw = event?.summary;
    return {
        generationType: typeof generationType === 'string' && generationType ? generationType : undefined,
        generatedRows: finiteOrUndefined(event?.generated_rows ?? event?.generatedRows),
        skippedRows: finiteOrUndefined(event?.skipped_rows ?? event?.skippedRows),
        skippedReasons,
        summary: typeof summaryRaw === 'string' && summaryRaw.trim() ? summaryRaw : undefined,
    };
}

/**
 * ``progressStatus`` fields carried for trajectory / summary-aware generation
 * cards. Empty object when the payload has none of them.
 */
export function generationSummaryExtra(normalized) {
    if (!normalized) return {};
    const extra = {};
    if (normalized.generationType) {
        extra.generationType = normalized.generationType;
        if (isTrajectoryGenerationType(normalized.generationType)) {
            extra.isTrajectoryGeneration = true;
        }
    }
    if (normalized.generatedRows !== undefined) extra.generatedRows = normalized.generatedRows;
    if (normalized.skippedRows !== undefined) extra.skippedRows = normalized.skippedRows;
    if (normalized.skippedReasons) extra.skippedReasons = normalized.skippedReasons;
    if (normalized.summary) extra.summary = normalized.summary;
    return extra;
}

/**
 * ``reason (n samples)`` lines for the details expander of a trajectory
 * warning card, most frequent first.
 */
export function buildTrajectorySkipDetailLines(skippedReasons) {
    if (!skippedReasons || typeof skippedReasons !== 'object') return [];
    return Object.entries(skippedReasons)
        .map(([reason, count]) => [reason, Number(count) || 0])
        .filter(([reason, count]) => reason && count > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([reason, count]) => `${reason} (${count} ${count === 1 ? 'sample' : 'samples'})`);
}

/**
 * Warning-card fields for a completed generation that skipped rows and
 * reported a ``summary``. Trajectory jobs get the trajectory title; agentic
 * FULL_SAMPLE runs that dropped rejected examples get the generic one.
 * Returns null when nothing was skipped or no summary is available.
 */
export function buildGenerationSummaryWarningFields(normalized) {
    if (!normalized?.summary) return null;
    if (!(normalized.skippedRows > 0)) return null;
    const isTrajectory = isTrajectoryGenerationType(normalized.generationType);
    return {
        isWarning: true,
        warningTitle: isTrajectory ? TRAJECTORY_WARNING_TITLE : GENERATED_EXAMPLES_SKIPPED_TITLE,
        warningMessage: normalized.summary,
        ...generationSummaryExtra(normalized),
    };
}

function isCheckingLabelsPhase(phase) {
    return phase === GENERATION_PHASE_CHECKING_LABELS || phase === 'checking_labels';
}

function isUnderstandingPhase(phase) {
    return phase === GENERATION_PHASE_UNDERSTANDING || phase === 'understanding_the_task';
}

/**
 * Coarse step for the three-row generation card. ``generating`` /
 * ``generating_samples``, the vendor-batch LABEL_ONLY phases
 * (``submitting_batch`` / ``batch_processing`` / ``saving_labels``) and any
 * phase this build does not know yet all land on the Generating row, so a new
 * backend phase can never push the card onto the wrong step. The raw phase is
 * kept separately as ``jobPhase`` for the human label.
 */
function normalizeGenerationPhase(phase) {
    if (isCheckingLabelsPhase(phase)) return GENERATION_PHASE_CHECKING_LABELS;
    if (isUnderstandingPhase(phase)) return GENERATION_PHASE_UNDERSTANDING;
    return GENERATION_PHASE_GENERATING;
}

/**
 * In-progress percents never round up to 100. 99.8 stays 99 until the step
 * actually finishes (exact 100 or a later phase).
 */
export function inProgressStepPercent(raw) {
    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0) return 0;
    if (value >= 100) return 100;
    return Math.min(99, Math.floor(value));
}

function percentFromJobFields(job) {
    const raw = Number(job?.progress);
    if (Number.isFinite(raw) && (raw > 0 || isCheckingLabelsPhase(job?.phase))) {
        return inProgressStepPercent(raw);
    }
    const processed = Number(job?.processed_prompts) || 0;
    const total = Number(job?.total_prompts) || 0;
    if (total > 0) {
        return inProgressStepPercent((processed / total) * 100);
    }
    return Number.isFinite(raw) ? inProgressStepPercent(raw) : 0;
}

/**
 * Active job payloads from GET ``ai-generation/status/{usecase_id}``.
 * Prefers the ``jobs`` array so two in-flight Generate with AI runs both restore.
 */
export function jobsFromGenerationStatus(result) {
    if (!result) return [];
    if (Array.isArray(result.jobs) && result.jobs.length) {
        return result.jobs.filter((job) => job && job.status !== 'cancelled');
    }
    if (result.active && result.status !== 'cancelled') return [result];
    return [];
}

/**
 * Percent for GET ``ai-generation/status/{usecase_id}`` (and the continue banner).
 * Prefers the job's ``progress`` field; falls back to processed/total prompts.
 */
export function percentFromGenerationStatus(result) {
    const jobs = jobsFromGenerationStatus(result);
    if (!jobs.length) return 0;
    return Math.max(...jobs.map((job) => percentFromJobFields(job)));
}

/**
 * FloatingProgressBar ``progressStatus`` item for an ``ai_gen_*`` generation row.
 * Same shape as the WebSocket ``updateGenerationProgress`` writer.
 */
export function generationProgressBarItem({
    usecaseId,
    datasetName,
    status,
    progress = 0,
    processedPrompts,
    totalPrompts,
    errorDetails,
    generationPhase,
    jobsAhead,
    extra = {},
} = {}) {
    const isFailed = status === 'failed';
    const isCompleted = status === 'completed';
    const isQueued = status === 'queued' && !isFailed && !isCompleted;
    // Raw backend phase (e.g. ``batch_processing``) for the human phase label;
    // ``phaseName`` below is the coarse step used for row mapping.
    const jobPhase = normalizeJobPhase(generationPhase || extra.generationPhase);
    const phaseName = normalizeGenerationPhase(
        generationPhase || extra.generationPhase || GENERATION_PHASE_GENERATING
    );
    const isChecking = !isCompleted && !isQueued && isCheckingLabelsPhase(phaseName);
    const isUnderstanding = !isCompleted && !isQueued && !isChecking && isUnderstandingPhase(phaseName);
    const stepPct = inProgressStepPercent(Number(progress) || 0);
    const understandPct = isQueued
        ? 0
        : isCompleted || isChecking || !isUnderstanding
            ? 100
            : stepPct;
    const generatingPct = isCompleted || isChecking
        ? 100
        : isUnderstanding || isQueued
            ? 0
            : isFailed
                ? stepPct
                : Math.max(1, stepPct);
    const checkingPct = isCompleted ? 100 : isChecking ? stepPct : 0;
    const resolvedPhase = isCompleted
        ? GENERATION_PHASE_CHECKING_LABELS
        : isChecking
            ? GENERATION_PHASE_CHECKING_LABELS
            : isUnderstanding
                ? GENERATION_PHASE_UNDERSTANDING
                : GENERATION_PHASE_GENERATING;
    const barPhase = isFailed
        ? isUnderstanding
            ? GENERATION_PHASE_UNDERSTANDING
            : isChecking
                ? GENERATION_PHASE_CHECKING_LABELS
                : 'uploading'
        : isCompleted
            ? 'completed'
            : isChecking
                ? GENERATION_PHASE_CHECKING_LABELS
                : isUnderstanding
                    ? GENERATION_PHASE_UNDERSTANDING
                    : 'uploading';
    return {
        datasetName: datasetName || 'AI Generated Dataset',
        usecaseId,
        isGeneration: true,
        isError: isFailed,
        isCompleted,
        isQueued,
        progress: isUnderstanding ? understandPct : generatingPct,
        generationPhase: isQueued ? phaseName : resolvedPhase,
        phase: isQueued ? 'uploading' : barPhase,
        understandProgress: isFailed && isUnderstanding ? stepPct : understandPct,
        uploadProgress: generatingPct,
        processingProgress: checkingPct,
        message: isFailed
            ? errorDetails ||
              (extra.isTrajectoryGeneration ? 'Trajectory generation failed' : 'Generation failed')
            : undefined,
        details: isFailed ? errorDetails : undefined,
        totalPrompts,
        processedPrompts,
        // Only carry the raw phase when the payload had one, so merging an
        // older status row without it keeps the last live phase.
        ...(jobPhase ? { jobPhase } : {}),
        // Only carry the key when a position is known, so merging an update
        // without one (e.g. a WebSocket event) keeps the last polled value.
        ...(typeof jobsAhead === 'number' && Number.isFinite(jobsAhead) ? { jobsAhead } : {}),
        ...extra,
    };
}

export function generationProgressBarItemFromStatus(result, usecaseId) {
    if (!result || !usecaseId) return null;
    if (result.status === 'cancelled') return null;
    if (result.active === false) return null;
    if (result.active !== true && !result.status) return null;
    // Status GET fallback for the WS relay: same snake_case keys as the
    // ``updateGenerationProgress`` payload.
    const normalized = normalizeGenerationProgressPayload(result);
    const warningFields =
        result.status === 'completed' ? buildGenerationSummaryWarningFields(normalized) : null;
    return generationProgressBarItem({
        usecaseId,
        datasetName: result.dataset_name,
        status: result.status,
        progress: percentFromJobFields(result),
        processedPrompts: result.processed_prompts,
        totalPrompts: result.total_prompts,
        errorDetails: result.error_details,
        generationPhase: result.phase,
        jobsAhead: result.jobs_ahead,
        extra: warningFields || generationSummaryExtra(normalized),
    });
}

/** One FloatingProgressBar item per active job in a status GET payload. */
export function generationProgressItemsFromStatus(result, usecaseId) {
    return jobsFromGenerationStatus(result)
        .map((job) => generationProgressBarItemFromStatus({ ...job, active: true }, usecaseId))
        .filter(Boolean);
}

function isCheckingItem(item) {
    return isCheckingLabelsPhase(item?.generationPhase) || isCheckingLabelsPhase(item?.phase);
}

function isUnderstandingItem(item) {
    return isUnderstandingPhase(item?.generationPhase) || isUnderstandingPhase(item?.phase);
}

function generationPhaseRank(item) {
    if (isCheckingItem(item)) return 2;
    if (isUnderstandingItem(item)) return 0;
    return 1;
}

function generatingStepPercent(item) {
    return Number(item?.uploadProgress ?? item?.progress) || 0;
}

function checkingStepPercent(item) {
    return Number(item?.processingProgress) || 0;
}

function understandingStepPercent(item) {
    return Number(item?.understandProgress ?? item?.progress) || 0;
}

function sameStepPercent(item) {
    if (isCheckingItem(item)) return checkingStepPercent(item);
    if (isUnderstandingItem(item)) return understandingStepPercent(item);
    return generatingStepPercent(item);
}

/**
 * Keep a live ``ai_gen_*`` percent from going backwards (e.g. when a second
 * teacher model starts) while generation is still in flight. Phase changes
 * (generating → checking labels) are allowed even if the new step percent is lower.
 */
export function applyMonotonicGenerationProgress(existing, nextItem) {
    if (!nextItem) return nextItem;
    if (
        !existing?.isGeneration
        || existing.isCompleted
        || existing.isError
        || nextItem.isCompleted
        || nextItem.isError
    ) {
        return nextItem;
    }
    const existingRank = generationPhaseRank(existing);
    const nextRank = generationPhaseRank(nextItem);
    if (nextRank > existingRank) {
        return nextItem;
    }
    if (nextRank < existingRank) {
        return existing;
    }
    const existingPct = sameStepPercent(existing);
    const nextPct = sameStepPercent(nextItem);
    if (existingPct > nextPct) {
        if (isCheckingItem(nextItem)) {
            return { ...nextItem, processingProgress: existingPct };
        }
        if (isUnderstandingItem(nextItem)) {
            return { ...nextItem, progress: existingPct, understandProgress: existingPct };
        }
        return {
            ...nextItem,
            progress: existingPct,
            uploadProgress: existingPct,
        };
    }
    return nextItem;
}

function promptCount(value) {
    const count = Number(value);
    return Number.isFinite(count) ? count : null;
}

/**
 * True when ``item`` tells the user something ``existing`` did not, even
 * though the step percent did not move: a new raw phase (vendor batch runs
 * sit at 0% through submitting_batch -> batch_processing -> saving_labels)
 * or fresher labeled / total counts.
 */
function hasNewerJobDetails(existing, item) {
    if (item.jobPhase && item.jobPhase !== existing.jobPhase) return true;
    const nextProcessed = promptCount(item.processedPrompts);
    const existingProcessed = promptCount(existing.processedPrompts);
    if (nextProcessed !== null && (existingProcessed === null || nextProcessed > existingProcessed)) {
        return true;
    }
    const nextTotal = promptCount(item.totalPrompts);
    const existingTotal = promptCount(existing.totalPrompts);
    return nextTotal !== null && nextTotal !== existingTotal;
}

function shouldSkipWeakerProgress(existing, item) {
    if (
        !existing?.isGeneration
        || existing.isCompleted
        || existing.isError
        || item.isCompleted
        || item.isError
    ) {
        return false;
    }
    if (!existing.isQueued && item.isQueued) return true;
    if (existing.isQueued && item.isQueued) return false;
    const existingRank = generationPhaseRank(existing);
    const nextRank = generationPhaseRank(item);
    if (existingRank > nextRank) return true;
    if (existingRank === nextRank && sameStepPercent(existing) >= sameStepPercent(item)) {
        return !hasNewerJobDetails(existing, item);
    }
    return false;
}

/**
 * Write an ``ai_gen_*`` item without clobbering a live WebSocket row that
 * already has equal or higher progress on the same step. Rows are keyed by
 * usecase + dataset so a second Generate with AI run does not replace the first.
 */
export function mergeGenerationProgressIntoStatus(prev, item, usecaseId, datasetName) {
    if (!item || !usecaseId) return prev;
    const name = datasetName || item.datasetName;
    const datasetKey = aiGenerationProgressKey(usecaseId, name);
    const legacyKey = aiGenerationProgressKey(usecaseId);
    let next = prev;
    let existing = prev?.[datasetKey];

    if (
        name
        && datasetKey !== legacyKey
        && prev?.[legacyKey]?.isGeneration
        && prev[legacyKey].datasetName === name
    ) {
        existing = { ...prev[legacyKey], ...(existing || {}) };
        next = { ...prev };
        delete next[legacyKey];
    }

    if (shouldSkipWeakerProgress(existing, item)) {
        if (next === prev && existing === prev?.[datasetKey]) return prev;
        return { ...next, [datasetKey]: existing };
    }
    return {
        ...next,
        [datasetKey]: { ...(existing || {}), ...item },
    };
}

export function seedGenerationProgressItem({ usecaseId, datasetName, status = 'queued', jobsAhead } = {}) {
    if (!usecaseId) return null;
    return generationProgressBarItem({
        usecaseId,
        datasetName,
        status,
        progress: 0,
        generationPhase: GENERATION_PHASE_UNDERSTANDING,
        jobsAhead,
    });
}

/**
 * Keep-records checkbox is for full-sample Generate with AI only (LABEL_ONLY
 * and TRAJECTORY runs fill in existing rows, they do not create records).
 */
export function shouldOfferKeepGeneratedRecords(file) {
    return Boolean(file?.isGeneration && !file?.isLabelGeneration && !file?.isTrajectoryGeneration);
}

/** Body for ``POST ai-generation/cancel``. Keep-records defaults on. */
export function buildCancelGenerationRequest({
    usecaseId,
    datasetName,
    clientId,
    keepGeneratedRecords = true,
} = {}) {
    return {
        usecase_id: usecaseId,
        dataset_name: datasetName || null,
        client_id: clientId || null,
        keep_generated_records: keepGeneratedRecords !== false,
    };
}
